import { FunctionComponent, useMemo, useState } from 'react';
import { SimpleObject } from '../../types';
import {
  EcgBandasTension,
  EcgBandaPu,
  EcgNivelTension,
  ECG_BANDAS_COLORS,
  ECG_CORREDOR_ABSOLUTO_DEFAULT
} from './estudioConexionResultadoTecnicoShared';

// ═══════════════════════════════════════════════════════════════════════════
// Boxplot por snapshot — NORM-018 A §3.1.2 G.3 / §3.1.3 IV
//
// Capas fijas en tensión:
//   - Corredor absoluto (0.85 / 1.10 p.u.): SIEMPRE visible (doble línea).
//   - Bandas relativas MT o BT: solo la del nivel elegido.
//   - Cajas: `tension_pu.{todos,mt,bt}` de `boxplot_snapshot` (schema 1.1).
//
// Fallback schema 1.0: si no vienen splits por nivel, se dibuja una sola vista
// "Todos los buses" + bandas desde `bandas_tension_pu_legacy`.
// ═══════════════════════════════════════════════════════════════════════════

export type EcgSnapshotBoxplotCategoria = 'tension' | 'lineas' | 'trafos';

function pick<T>(o: SimpleObject | null | undefined, snake: string, camel: string): T | undefined {
  if (!o || typeof o !== 'object') return undefined;
  const a = o[snake];
  if (a !== undefined && a !== null) return a as T;
  const b = o[camel];
  if (b !== undefined && b !== null) return b as T;
  return undefined;
}

type Five = { n: number; min: number; q1: number; med: number; q3: number; max: number } | null;

function parseFive(m: SimpleObject | null | undefined): Five {
  if (!m || typeof m !== 'object') return null;
  const n = Number(pick(m, 'n', 'n'));
  if (!Number.isFinite(n) || n <= 0) return null;
  const min = num(pick(m, 'min', 'min'));
  const q1 = num(pick(m, 'q1', 'q1'));
  const med = num(pick(m, 'median', 'median'));
  const q3 = num(pick(m, 'q3', 'q3'));
  const max = num(pick(m, 'max', 'max'));
  if (min === null || q1 === null || med === null || q3 === null || max === null) return null;
  return { n, min, q1, med, q3, max };
}

function num(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
}

/**
 * Devuelve los five-number summaries por nivel para una rama (`sin_proyecto` /
 * `con_proyecto`) del boxplot snapshot. Para categoría `tension` soporta
 * schema 1.1 (`tension_pu.{todos,mt,bt}`) y schema 1.0 (objeto plano = todos).
 */
function fiveByNivel(
  rama: SimpleObject | null | undefined,
  cat: EcgSnapshotBoxplotCategoria
): { todos: Five; mt: Five; bt: Five } {
  if (!rama) return { todos: null, mt: null, bt: null };
  const obj = pick<SimpleObject>(
    rama,
    cat === 'tension' ? 'tension_pu' : cat === 'lineas' ? 'lineas_pu' : 'trafos_pu',
    cat === 'tension' ? 'tensionPu' : cat === 'lineas' ? 'lineasPu' : 'trafosPu'
  );
  if (!obj) return { todos: null, mt: null, bt: null };

  // Schema 1.1: objeto con {todos,mt,bt}
  const todosObj = pick<SimpleObject>(obj, 'todos', 'todos');
  const mtObj = pick<SimpleObject>(obj, 'mt', 'mt');
  const btObj = pick<SimpleObject>(obj, 'bt', 'bt');
  const parseTodos = parseFive(todosObj);
  const parseMt = parseFive(mtObj);
  const parseBt = parseFive(btObj);
  if (parseTodos || parseMt || parseBt) {
    return { todos: parseTodos, mt: parseMt, bt: parseBt };
  }
  // Schema 1.0: objeto plano = todos
  return { todos: parseFive(obj), mt: null, bt: null };
}

function yScale(v: number, y0: number, y1: number, top: number, innerH: number): number {
  if (y1 <= y0) return top + innerH / 2;
  const t = (v - y0) / (y1 - y0);
  return top + innerH * (1 - t);
}

const COL_SIN = '#6b7280';
const COL_CON = '#00708d';

// Tipografías de marca para el SVG (atributos de presentación: no admiten var()).
const SVG_FONT_MONO = "'JetBrains Mono', ui-monospace, monospace";
const SVG_FONT_BODY = "'Source Sans 3', system-ui, sans-serif";
const SVG_FONT_DISPLAY = "'Archivo', 'Source Sans 3', system-ui, sans-serif";

const W = 640;
const H = 340;
const PAD_L = 52;
const PAD_R = 20;
const PAD_T = 28;
const PAD_B = 48;
const INNER_W = W - PAD_L - PAD_R;
const INNER_H = H - PAD_T - PAD_B;

type ColDraw = { cx: number; five: Five; label: string; color: string };

export type EcgSnapshotBoxplotModo = 'comparativo' | 'unico';

/**
 * Extrae bandas desde el boxplot_snapshot (schema 1.1 `bandas_tension_pu` con
 * {absoluto, mt, bt}) como complemento/fallback al bandas_tension raíz.
 */
function bandasDesdeBoxplot(boxplotRoot: SimpleObject | null): Partial<EcgBandasTension> | null {
  const b = pick<SimpleObject>(boxplotRoot, 'bandas_tension_pu', 'bandasTensionPu');
  if (!b || typeof b !== 'object') return null;
  const abs = parseBanda(b.absoluto ?? b.absolutoPu);
  const mt = parseBanda(b.mt ?? b.mtPu);
  const bt = parseBanda(b.bt ?? b.btPu);
  const res: Partial<EcgBandasTension> = {
    absolutoPu: abs ?? undefined,
    mtPu: mt ?? null,
    btPu: bt ?? null
  };
  return res;
}

function parseBanda(x: unknown): EcgBandaPu | null {
  if (!x || typeof x !== 'object') return null;
  const o = x as SimpleObject;
  const min = Number(o.min);
  const max = Number(o.max);
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return null;
  return { min, max };
}

/** Fallback schema 1.0: `bandas_tension_pu_legacy` con {v_min_pu, v_max_pu}. */
function legacyBandaPu(boxplotRoot: SimpleObject | null): EcgBandaPu | null {
  const b = pick<SimpleObject>(boxplotRoot, 'bandas_tension_pu_legacy', 'bandasTensionPuLegacy');
  if (!b || typeof b !== 'object') return null;
  const min = Number(b.v_min_pu ?? b.vMinPu);
  const max = Number(b.v_max_pu ?? b.vMaxPu);
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return null;
  return { min, max };
}

/** Selecciona el Five a dibujar según nivel actual. */
function fiveForNivel(
  group: { todos: Five; mt: Five; bt: Five },
  nivel: EcgNivelTension
): Five {
  if (nivel === 'mt') return group.mt;
  if (nivel === 'bt') return group.bt;
  return group.todos;
}

const EstudioConexionSnapshotBoxplots: FunctionComponent<{
  boxplotRoot: SimpleObject;
  modo?: EcgSnapshotBoxplotModo;
  /** Rama del payload a dibujar en modo `unico`. Default: `con_proyecto`. */
  ramaUnica?: 'sin_proyecto' | 'con_proyecto';
  /** Etiqueta del eje X bajo la caja en modo `unico`. */
  etiquetaUnica?: string;
  /** Bandas del artefacto raíz (§3.1.3 II). Si no se pasa, se usa `bandas_tension_pu` del boxplot o fallback. */
  bandas?: EcgBandasTension;
}> = ({ boxplotRoot, modo = 'comparativo', ramaUnica = 'con_proyecto', etiquetaUnica, bandas }) => {
  const [categoria, setCategoria] = useState<EcgSnapshotBoxplotCategoria>('tension');
  const [nivel, setNivel] = useState<EcgNivelTension>('mt');

  const sinR = pick<SimpleObject>(boxplotRoot, 'sin_proyecto', 'sinProyecto');
  const conR = pick<SimpleObject>(boxplotRoot, 'con_proyecto', 'conProyecto');

  // Resolución de bandas: prioriza prop `bandas` (root artefacto), luego
  // `bandas_tension_pu` del boxplot, y como último recurso el legacy.
  const bandasResueltas: EcgBandasTension = useMemo(() => {
    const fromBox = bandasDesdeBoxplot(boxplotRoot);
    const abs = bandas?.absolutoPu ?? fromBox?.absolutoPu ?? { ...ECG_CORREDOR_ABSOLUTO_DEFAULT };
    const mt = bandas?.mtPu ?? fromBox?.mtPu ?? null;
    const bt = bandas?.btPu ?? fromBox?.btPu ?? null;
    const legacyMT = !mt && !bt ? legacyBandaPu(boxplotRoot) : null;
    const schemaLegacy = bandas ? bandas.schemaLegacy : !fromBox;
    return {
      densidad: bandas?.densidad ?? null,
      densidadFuente: bandas?.densidadFuente ?? null,
      absolutoPu: abs,
      mtPu: mt ?? (legacyMT ? legacyMT : null),
      btPu: bt,
      deltaVPcMaxPct: bandas?.deltaVPcMaxPct ?? null,
      origenNormativo: bandas?.origenNormativo ?? null,
      schemaLegacy: Boolean(schemaLegacy)
    };
  }, [boxplotRoot, bandas]);

  const fivesSin = useMemo(() => fiveByNivel(sinR, categoria), [sinR, categoria]);
  const fivesCon = useMemo(() => fiveByNivel(conR, categoria), [conR, categoria]);

  // Niveles disponibles para el filtro (solo aplica a tensión)
  const hayMt =
    categoria === 'tension' && (fivesSin.mt !== null || fivesCon.mt !== null) && !!bandasResueltas.mtPu;
  const hayBt =
    categoria === 'tension' && (fivesSin.bt !== null || fivesCon.bt !== null) && !!bandasResueltas.btPu;
  const hayTodos =
    fivesSin.todos !== null || fivesCon.todos !== null;

  const nivelEfectivo: EcgNivelTension = useMemo(() => {
    if (categoria !== 'tension') return 'todos';
    if (nivel === 'mt' && hayMt) return 'mt';
    if (nivel === 'bt' && hayBt) return 'bt';
    if (hayMt) return 'mt';
    if (hayBt) return 'bt';
    return 'todos';
  }, [categoria, nivel, hayMt, hayBt]);

  const refUtil = num(pick(boxplotRoot, 'referencia_utilizacion_pu', 'referenciaUtilizacionPu')) ?? 1.0;

  const { cols, y0, y1, bandRect, refLineY } = useMemo(() => {
    const fsin = fiveForNivel(fivesSin, nivelEfectivo);
    const fcon = fiveForNivel(fivesCon, nivelEfectivo);

    const colsDraft: ColDraw[] =
      modo === 'unico'
        ? (() => {
            const rama = ramaUnica === 'sin_proyecto' ? fsin : fcon;
            const color = ramaUnica === 'sin_proyecto' ? COL_SIN : COL_CON;
            const label = etiquetaUnica ?? (ramaUnica === 'sin_proyecto' ? 'Sin proyecto' : 'Con proyecto');
            return [{ cx: PAD_L + INNER_W * 0.5, five: rama, label, color }];
          })()
        : [
            { cx: PAD_L + INNER_W * 0.28, five: fsin, label: 'Sin proyecto', color: COL_SIN },
            { cx: PAD_L + INNER_W * 0.72, five: fcon, label: 'Con proyecto', color: COL_CON }
          ];

    const bandaRelativa: EcgBandaPu | null =
      categoria === 'tension'
        ? nivelEfectivo === 'mt'
          ? bandasResueltas.mtPu
          : nivelEfectivo === 'bt'
            ? bandasResueltas.btPu
            : null
        : null;

    const vals: number[] = [];
    for (const c of colsDraft) {
      if (c.five) {
        vals.push(c.five.min, c.five.max, c.five.q1, c.five.q3);
      }
    }
    if (categoria === 'tension') {
      vals.push(bandasResueltas.absolutoPu.min, bandasResueltas.absolutoPu.max);
      if (bandaRelativa) vals.push(bandaRelativa.min, bandaRelativa.max);
    } else {
      vals.push(0, refUtil);
    }

    let yLo = vals.length ? Math.min(...vals) : 0;
    let yHi = vals.length ? Math.max(...vals) : 1;
    const pad = (yHi - yLo) * 0.08 || 0.05;
    yLo -= pad;
    yHi += pad;
    if (categoria === 'lineas' || categoria === 'trafos') {
      yLo = Math.min(yLo, 0);
      yHi = Math.max(yHi, refUtil * 1.15, 1.05);
    }
    if (yHi <= yLo) {
      yLo = 0;
      yHi = 1;
    }

    let bandRectEl: JSX.Element | null = null;
    if (categoria === 'tension' && bandaRelativa) {
      const yTop = yScale(bandaRelativa.max, yLo, yHi, PAD_T, INNER_H);
      const yBot = yScale(bandaRelativa.min, yLo, yHi, PAD_T, INNER_H);
      const fill =
        nivelEfectivo === 'mt'
          ? 'rgba(224, 108, 0, 0.10)'
          : nivelEfectivo === 'bt'
            ? 'rgba(0, 114, 178, 0.10)'
            : 'rgba(34, 197, 94, 0.10)';
      const stroke =
        nivelEfectivo === 'mt'
          ? 'rgba(224, 108, 0, 0.55)'
          : nivelEfectivo === 'bt'
            ? 'rgba(0, 114, 178, 0.55)'
            : 'rgba(22, 163, 74, 0.55)';
      bandRectEl = (
        <rect
          x={PAD_L}
          y={Math.min(yTop, yBot)}
          width={INNER_W}
          height={Math.abs(yBot - yTop)}
          fill={fill}
          stroke={stroke}
          strokeWidth={1}
        />
      );
    }

    const overlays: JSX.Element[] = [];
    if (categoria === 'tension') {
      // Corredor absoluto — siempre visible, estilo fuerte.
      const yAbsMin = yScale(bandasResueltas.absolutoPu.min, yLo, yHi, PAD_T, INNER_H);
      const yAbsMax = yScale(bandasResueltas.absolutoPu.max, yLo, yHi, PAD_T, INNER_H);
      overlays.push(
        <line
          key='abs-min'
          x1={PAD_L}
          x2={W - PAD_R}
          y1={yAbsMin}
          y2={yAbsMin}
          stroke={ECG_BANDAS_COLORS.absoluto}
          strokeWidth={1.8}
        />,
        <line
          key='abs-max'
          x1={PAD_L}
          x2={W - PAD_R}
          y1={yAbsMax}
          y2={yAbsMax}
          stroke={ECG_BANDAS_COLORS.absoluto}
          strokeWidth={1.8}
        />
      );
      if (bandaRelativa) {
        const colorRel =
          nivelEfectivo === 'mt' ? ECG_BANDAS_COLORS.mt : ECG_BANDAS_COLORS.bt;
        const yRelMin = yScale(bandaRelativa.min, yLo, yHi, PAD_T, INNER_H);
        const yRelMax = yScale(bandaRelativa.max, yLo, yHi, PAD_T, INNER_H);
        overlays.push(
          <line
            key='rel-min'
            x1={PAD_L}
            x2={W - PAD_R}
            y1={yRelMin}
            y2={yRelMin}
            stroke={colorRel}
            strokeDasharray='4 3'
            strokeWidth={1}
          />,
          <line
            key='rel-max'
            x1={PAD_L}
            x2={W - PAD_R}
            y1={yRelMax}
            y2={yRelMax}
            stroke={colorRel}
            strokeDasharray='4 3'
            strokeWidth={1}
          />
        );
      }
    } else {
      const yRef = yScale(refUtil, yLo, yHi, PAD_T, INNER_H);
      overlays.push(
        <line
          key='util-ref'
          x1={PAD_L}
          x2={W - PAD_R}
          y1={yRef}
          y2={yRef}
          stroke='#991b1b'
          strokeDasharray='6 4'
          strokeWidth={1.5}
        />
      );
    }

    return { cols: colsDraft, y0: yLo, y1: yHi, bandRect: bandRectEl, refLineY: <>{overlays}</> };
  }, [fivesSin, fivesCon, categoria, modo, ramaUnica, etiquetaUnica, bandasResueltas, nivelEfectivo, refUtil]);

  const yAxisTicks = useMemo(() => {
    const nTicks = 5;
    const out: number[] = [];
    for (let i = 0; i < nTicks; i++) {
      out.push(y0 + ((y1 - y0) * i) / (nTicks - 1));
    }
    return out;
  }, [y0, y1]);

  const yLabel =
    categoria === 'tension'
      ? 'Tensión en buses (p.u.)'
      : categoria === 'lineas'
        ? 'Utilización de líneas (p.u.)'
        : 'Utilización de transformadores (p.u.)';

  const titulo =
    modo === 'unico'
      ? 'Distribución snapshot'
      : 'Distribución snapshot (comparación sin proyecto / con proyecto)';

  const filtroNivelHabilitado = categoria === 'tension' && (hayMt || hayBt) && !bandasResueltas.schemaLegacy;

  const avisoLegacy = categoria === 'tension' && bandasResueltas.schemaLegacy;

  return (
    <div className='ecg-snapshot-boxplot-root' style={{ marginTop: '1.25rem' }}>
      <h4 className='vdr-result-chart-title'>{titulo}</h4>
      <div className='ecg-form-row' style={{ gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        <div className='ecg-form-field' style={{ maxWidth: 320 }}>
          <label className='ecg-label' htmlFor='ecg-snapshot-box-cat'>
            Magnitud
          </label>
          <select
            id='ecg-snapshot-box-cat'
            className='ecg-input'
            value={categoria}
            onChange={(e) => setCategoria(e.target.value as EcgSnapshotBoxplotCategoria)}>
            <option value='tension'>Tensión en buses (p.u.)</option>
            <option value='lineas'>Utilización de líneas (p.u.)</option>
            <option value='trafos'>Utilización de transformadores (p.u.)</option>
          </select>
        </div>
        {filtroNivelHabilitado && (
          <div className='ecg-form-field' style={{ maxWidth: 240 }}>
            <label className='ecg-label' htmlFor='ecg-snapshot-box-nivel'>
              Nivel de tensión
            </label>
            <select
              id='ecg-snapshot-box-nivel'
              className='ecg-input'
              value={nivel}
              onChange={(e) => setNivel(e.target.value as EcgNivelTension)}>
              <option value='mt' disabled={!hayMt}>
                Media tensión{!hayMt ? ' (sin buses)' : ''}
              </option>
              <option value='bt' disabled={!hayBt}>
                Baja tensión{!hayBt ? ' (sin buses)' : ''}
              </option>
              <option value='todos' disabled={!hayTodos}>
                Todos los buses
              </option>
            </select>
          </div>
        )}
      </div>
      {avisoLegacy && (
        <p className='ecg-hint ecg-hint-warn' style={{ margin: '0 0 0.5rem' }}>
          Estudio pre-NORM-018 A: vista combinada (todos los buses) con corredor absoluto. Re-ejecute el
          estudio para ver bandas relativas por nivel.
        </p>
      )}
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          style={{ width: '100%', maxWidth: 720, height: 'auto', display: 'block' }}
          role='img'
          aria-label={`Boxplot snapshot: ${yLabel}`}>
          {bandRect}
          {yAxisTicks.map((tv, i) => {
            const py = yScale(tv, y0, y1, PAD_T, INNER_H);
            return (
              <g key={i}>
                <line x1={PAD_L} x2={W - PAD_R} y1={py} y2={py} stroke='#dce6ea' strokeWidth={1} />
                <text x={PAD_L - 8} y={py + 4} textAnchor='end' fontSize={10.5} fill='#5e6e75' fontFamily={SVG_FONT_MONO}>
                  {tv.toFixed(3)}
                </text>
              </g>
            );
          })}
          {refLineY}
          {cols.map((col, idx) => {
            const { cx, five, label, color } = col;
            const bw = 56;
            if (!five) {
              return (
                <g key={idx}>
                  <text x={cx} y={PAD_T + INNER_H / 2} textAnchor='middle' fontSize={12} fill='#7f9199' fontFamily={SVG_FONT_BODY}>
                    Sin datos
                  </text>
                  <text x={cx} y={H - 16} textAnchor='middle' fontSize={12.5} fill='#22313a' fontWeight={600} fontFamily={SVG_FONT_BODY}>
                    {label}
                  </text>
                </g>
              );
            }
            const yMin = yScale(five.min, y0, y1, PAD_T, INNER_H);
            const yQ1 = yScale(five.q1, y0, y1, PAD_T, INNER_H);
            const yMed = yScale(five.med, y0, y1, PAD_T, INNER_H);
            const yQ3 = yScale(five.q3, y0, y1, PAD_T, INNER_H);
            const yMax = yScale(five.max, y0, y1, PAD_T, INNER_H);
            const topR = Math.min(yQ1, yQ3);
            const hR = Math.abs(yQ3 - yQ1);
            return (
              <g key={idx}>
                <line x1={cx} x2={cx} y1={yMin} y2={yMax} stroke={color} strokeWidth={2} />
                <line x1={cx - bw / 2} x2={cx + bw / 2} y1={yMin} y2={yMin} stroke={color} strokeWidth={2} />
                <line x1={cx - bw / 2} x2={cx + bw / 2} y1={yMax} y2={yMax} stroke={color} strokeWidth={2} />
                <rect
                  x={cx - bw / 2}
                  y={topR}
                  width={bw}
                  height={Math.max(hR, 1)}
                  fill={`${color}33`}
                  stroke={color}
                  strokeWidth={2}
                />
                <line
                  x1={cx - bw / 2}
                  x2={cx + bw / 2}
                  y1={yMed}
                  y2={yMed}
                  stroke={color}
                  strokeWidth={2.5}
                />
                <text x={cx} y={H - 16} textAnchor='middle' fontSize={12.5} fill='#22313a' fontWeight={600} fontFamily={SVG_FONT_BODY}>
                  {label}
                </text>
                <text x={cx} y={PAD_T - 4} textAnchor='middle' fontSize={10} fill='#5e6e75' fontFamily={SVG_FONT_MONO}>
                  n = {five.n}
                </text>
              </g>
            );
          })}
          <text x={W / 2} y={12} textAnchor='middle' fontSize={12.5} fill='#0b0e10' fontWeight={700} fontFamily={SVG_FONT_DISPLAY}>
            {yLabel}
          </text>
        </svg>
      </div>
      <p className='ecg-hint' style={{ marginTop: '0.5rem', fontSize: '0.82rem' }}>
        Caja: Q1–Q3; línea central: mediana; bigotes: mínimo y máximo.
        {categoria === 'tension'
          ? ' Líneas rojas: corredor absoluto 0,85–1,10 p.u. Franja coloreada y líneas discontinuas: bandas relativas del nivel seleccionado (según densidad del estudio).'
          : ' Línea roja discontinua: referencia 1,0 p.u. de utilización nominal.'}
      </p>
    </div>
  );
};

export default EstudioConexionSnapshotBoxplots;

/** Extrae el objeto `boxplot_snapshot` de un `datos_json` de fila `flujo_snapshot`. */
export function ecgExtractBoxplotSnapshot(dj: SimpleObject | null | undefined): SimpleObject | null {
  const bp = pick<SimpleObject>(dj, 'boxplot_snapshot', 'boxplotSnapshot');
  if (!bp || typeof bp !== 'object') return null;
  return bp;
}
